import { Pencil, FileText, Building, MapPin } from 'lucide-react';
import { useProfile } from '../../../../../context/profile/ProfileContext';
import CustomInput from '../../../../../components/forms/CustomInput';

const SubscriptionGstCard = () => {
  const {
    formData,
    isEditingGst,
    setIsEditingGst,
    gstinValue,
    setGstinValue,
    gstNameValue,
    setGstNameValue,
    gstAddressValue,
    setGstAddressValue,
    handleSaveGst
  } = useProfile();

  return (
    <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-100 p-4 sm:p-6 lg:p-7 shadow-sm">
      <div className="flex justify-between items-center border-b border-slate-100 pb-3.5 sm:pb-4 mb-3.5 sm:mb-4 gap-2">
        <div>
          <h2 className="font-semibold text-slate-900 text-sm sm:text-base">GST details</h2>
          <div className="text-xs text-slate-500 mt-0.5 font-normal">For tax invoices &amp; claiming ITC</div>
        </div>
        {!isEditingGst ? (
          <button
            onClick={() => {
              setGstinValue(formData.gstin || '');
              setGstNameValue(formData.gst_legal_name || formData.business_name || '');
              setGstAddressValue(formData.gst_address || formData.address || '');
              setIsEditingGst(true);
            }}
            className="px-3 sm:px-3.5 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-xl font-semibold text-xs border border-slate-200 hover:border-slate-300 transition-all flex items-center gap-1.5 shadow-2xs active:scale-95 cursor-pointer whitespace-nowrap"
          >
            <Pencil size={12} className="text-slate-500" />
            <span>{formData.gstin ? 'Edit' : 'Add'}</span>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEditingGst(false)}
              className="px-2.5 sm:px-3 py-1 text-slate-500 hover:text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-100 transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveGst}
              className="px-3 sm:px-3.5 py-1 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-lg shadow-sm shadow-[#067978]/20 transition-all active:scale-95 cursor-pointer"
            >
              Save
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {!isEditingGst ? (
          <>
            {formData.gstin ? (
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-[#067978] shrink-0" />
                  <span className="font-mono bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-md text-xs tracking-wider text-[#067978] font-semibold uppercase break-all">
                    {formData.gstin}
                  </span>
                </div>
                {(formData.gst_legal_name || formData.business_name) && (
                  <div className="flex items-start gap-2 pt-1 text-slate-700 font-medium">
                    <Building size={14} className="text-slate-400 shrink-0 mt-0.5" />
                    <span className="break-words">{formData.gst_legal_name || formData.business_name}</span>
                  </div>
                )}
                {(formData.gst_address || formData.address) && (
                  <div className="flex items-start gap-2 text-slate-500">
                    <MapPin size={14} className="text-slate-400 shrink-0 mt-0.5" />
                    <span className="line-clamp-2 break-words">{formData.gst_address || formData.address}</span>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setGstinValue(formData.gstin || '');
                  setGstNameValue(formData.gst_legal_name || formData.business_name || '');
                  setGstAddressValue(formData.gst_address || formData.address || '');
                  setIsEditingGst(true);
                }}
                className="text-xs text-[#067978] font-medium hover:underline flex items-center gap-1.5 cursor-pointer"
              >
                <FileText size={15} />
                <span>+ Add GSTIN, Name &amp; Address</span>
              </button>
            )}
            <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
              Used on every invoice to claim Input Tax Credit (ITC).
            </p>
          </>
        ) : (
          <div className="space-y-3">
            <CustomInput
              label="GST Number (GSTIN)"
              value={gstinValue}
              onChange={(e) => setGstinValue(e.target.value.toUpperCase())}
              placeholder="e.g. 27AABCO1234A1Z5"
              required={true}
              mono={true}
              icon={FileText}
            />
            <CustomInput
              label="Registered Legal Name"
              value={gstNameValue}
              onChange={(e) => setGstNameValue(e.target.value)}
              placeholder="e.g. Acme Travels Pvt Ltd"
              icon={Building}
            />
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">Registered Address</label>
              <textarea
                rows="2"
                value={gstAddressValue}
                onChange={(e) => setGstAddressValue(e.target.value)}
                placeholder="e.g. 101, Business Park, Mumbai"
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-normal text-slate-700 transition-all outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-none shadow-sm"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionGstCard;
